import FormatCurrency from 'components/Common/FormatCurrency'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  price?: number
  propertyFees?: number
  flightPrice?: number
  duration: number
  adultsNumber: number
  withFlights?: boolean
  displayPricingAsPerPerson: boolean
  rooms: Array<App.RoomOccupants>
}

function CalendarPriceBlurb(props: Props) {
  const { price, propertyFees, flightPrice, duration, adultsNumber, withFlights, displayPricingAsPerPerson, rooms = EmptyArray } = props
  const intl = useIntl()

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    nights: duration,
    roomsNumber: rooms.length,
    withFlights: !!withFlights,
    flightPrice: flightPrice ?? 0,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  const showFlightsAfterNights = !!withFlights && !!flightPrice
  const showFlightsInSuffix = !!withFlights && !flightPrice && !displayPricingAsPerPerson
  const isMultiRoom = rooms.length > 1

  const blurb = useMemo(() => {
    const boldValue = (chunks: React.ReactNode) => (
      <BodyText key="bold" as="span" variant="medium" colour="neutral-one" weight="bold">{chunks}</BodyText>
    )
    const captionValue = (chunks: React.ReactNode) => (
      <Caption key="caption" variant="medium" as="span">{chunks}</Caption>
    )
    const priceValue = <FormatCurrency key="price" value={total} />

    if (isMultiRoom) {
      if (displayPricingAsPerPerson && showFlightsAfterNights) {
        // eslint-disable-next-line formatjs/no-multiple-plurals
        return intl.formatMessage(
          {
            id: 'r5Be5CeA3gxDKQ36DZZd',
            defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}, {nightCount, plural, one {# night} other {# nights}} + flights from <bold>{price}<caption>{saleUnit}</caption></bold>',
            description: 'Calendar price blurb for multiple rooms, per-person pricing with flights included in price',
          },
          { roomCount: rooms.length, nightCount: duration, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
        )
      }

      if (displayPricingAsPerPerson) {
        // eslint-disable-next-line formatjs/no-multiple-plurals
        return intl.formatMessage(
          {
            id: 'xM0aQAQ6xDgjqGMPE8Ky',
            defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}, {nightCount, plural, one {# night} other {# nights}} from <bold>{price}<caption>{saleUnit}</caption></bold>',
            description: 'Calendar price blurb for multiple rooms, per-person pricing without flights',
          },
          { roomCount: rooms.length, nightCount: duration, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
        )
      }

      if (showFlightsAfterNights) {
        // eslint-disable-next-line formatjs/no-multiple-plurals
        return intl.formatMessage(
          {
            id: '0LfKWnvGrN1Hfe92sBVT',
            defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}, {nightCount, plural, one {# night} other {# nights}} + flights from <bold>{price}</bold> {saleUnit}.',
            description: 'Calendar price blurb for multiple rooms, total pricing with flights included in price',
          },
          { roomCount: rooms.length, nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
        )
      }

      if (showFlightsInSuffix) {
        // eslint-disable-next-line formatjs/no-multiple-plurals
        return intl.formatMessage(
          {
            id: '7zFICNZ-3qKD-HFmUffx',
            defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}, {nightCount, plural, one {# night} other {# nights}} from <bold>{price}</bold> {saleUnit} + flights.',
            description: 'Calendar price blurb for multiple rooms, total pricing with flights added separately',
          },
          { roomCount: rooms.length, nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
        )
      }

      // eslint-disable-next-line formatjs/no-multiple-plurals
      return intl.formatMessage(
        {
          id: 'y4gxDfnX_rIhgaqur8Ks',
          defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}, {nightCount, plural, one {# night} other {# nights}} from <bold>{price}</bold> {saleUnit}.',
          description: 'Calendar price blurb for multiple rooms, total pricing without flights',
        },
        { roomCount: rooms.length, nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    if (displayPricingAsPerPerson && showFlightsAfterNights) {
      return intl.formatMessage(
        {
          id: 'zYUZKt8N01AgDrMFQUHE',
          defaultMessage: '{nightCount, plural, one {# night} other {# nights}} + flights from <bold>{price}<caption>{saleUnit}</caption></bold>',
          description: 'Calendar price blurb for single room, per-person pricing with flights included in price',
        },
        { nightCount: duration, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
      )
    }

    if (displayPricingAsPerPerson) {
      return intl.formatMessage(
        {
          id: 'X4HjO5nC_7drus-_hj0Q',
          defaultMessage: '{nightCount, plural, one {# night} other {# nights}} from <bold>{price}<caption>{saleUnit}</caption></bold>',
          description: 'Calendar price blurb for single room, per-person pricing without flights',
        },
        { nightCount: duration, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
      )
    }

    if (showFlightsAfterNights) {
      return intl.formatMessage(
        {
          id: '_bI23tRVXuQ5Wcc_4vde',
          defaultMessage: '{nightCount, plural, one {# night} other {# nights}} + flights from <bold>{price}</bold> {saleUnit}.',
          description: 'Calendar price blurb for single room, total pricing with flights included in price',
        },
        { nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    if (showFlightsInSuffix) {
      return intl.formatMessage(
        {
          id: 'EvVKlo6TU6KrOaKX_CLm',
          defaultMessage: '{nightCount, plural, one {# night} other {# nights}} from <bold>{price}</bold> {saleUnit} + flights.',
          description: 'Calendar price blurb for single room, total pricing with flights added separately',
        },
        { nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    return intl.formatMessage(
      {
        id: 'yyvb3Wr0cW9Ru6ynj-C_',
        defaultMessage: '{nightCount, plural, one {# night} other {# nights}} from <bold>{price}</bold> {saleUnit}.',
        description: 'Calendar price blurb for single room, total pricing without flights',
      },
      { nightCount: duration, price: priceValue, saleUnit, bold: boldValue },
    )
  }, [intl, total, displayPricingAsPerPerson, showFlightsAfterNights, showFlightsInSuffix, isMultiRoom, rooms.length, duration, saleUnit])

  return <Group
    direction="vertical"
    gap={4}
    tabletDirection="horizontal"
    tabletHorizontalAlign="space-between"
  >
    <div>
      <BodyText variant="medium" colour="neutral-two">
        {blurb}
      </BodyText>
      <BodyText variant="medium" colour="neutral-one">
        {intl.formatMessage({
          id: 'k__r_VMoM7NwLhPJJOMc',
          defaultMessage: '* Price is indicative. Select your dates to view best available rates',
          description: 'Disclaimer about indicative pricing on bedbank calendar date picker',
        })}
      </BodyText>
    </div>
    <BodyTextBlock variant="medium" startIcon={<SolidFireIcon colour="urgency" />}>
      {intl.formatMessage({
        id: 'S0AgJUjekhVSOvPlfWzB',
        defaultMessage: 'Limited Time Special',
        description: 'Label for limited time special promotion on bedbank calendar date picker',
      })}
    </BodyTextBlock>
  </Group>
}

export default withOfferPageState((state) => ({
  duration: state.duration,
  withFlights: state.flights,
  rooms: state.rooms,
}), CalendarPriceBlurb)
